
import { getHubData } from "@/app/infrastructures/misc/Cookies";
import { formatTimeNumber } from "@/app/infrastructures/misc/Utils";
import MultipleSelect from "@/app/ui/components/multiple-select/index.vue";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { CargoController } from "@/app/ui/controllers/CargoController";
import { HubController } from "@/app/ui/controllers/HubController";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { OptionsClass } from "@/domain/entities/MainApp";
import { CargoData } from "@/domain/entities/Cargo";
import { RequestHubList } from "@/domain/entities/Hub";
import { debounce } from "lodash";
import { Options, prop, Vue } from "vue-class-component";
import { FlagsPermissionCargo } from "@/feature-flags/flags-permission-cargo";
import ModalDownloadListCargo from "./modal-download-list-cargo.vue";

class Props {
  bookingType = prop<"success" | "failed" | "in-progress" | string>({
    default: "",
    type: String
  });
}

@Options({
  emits: ["onChangeFilter"],
  components: {
    MultipleSelect,
    ModalDownloadListCargo
  }
})
export default class CargoFilterSection extends Vue.with(Props) {
  // Filter Search
  search = "";

  get searchValue() {
    return CargoController.search;
  }

  get shouldShowSummary() {
    return FlagsPermissionCargo.permission_cargo_dispute_summary_enable.isEnabled();
  }

  get isSuccessBookingTab() {
    return this.$route?.name === "booking-success";
  }

  get cargoSuccessSummaryData() {
    return CargoController.cargoSuccessSummaryData;
  }

  get cargoTypes() {
    return CargoController.cargoTypes;
  }

  onSearch(value: string) {
    CargoController.searchAction(value);
  }

  onClearSearch() {
    CargoController.searchAction("");
    this.search = "";
  }

  // Advance Filter
  get advancedFilterData() {
    return CargoController.advanceFilterData;
  }

  get typeList() {
    return [
      {
        name: this.$t("Pesawat"),
        value: "plane"
      },
      {
        name: this.$t("Truk"),
        value: "truck"
      },
      {
        name: this.$t("Kereta"),
        value: "train"
      },
      {
        name: this.$t("Kapal"),
        value: "ship"
      }
    ];
  }

  badgeSuccessFilterOptions = [
    new OptionsClass({
      name: "Pesawat",
      value: "plane"
    }),
    new OptionsClass({
      name: "Truk",
      value: "truck"
    }),
    new OptionsClass({
      name: "Kapal",
      value: "ship"
    })
  ];

  onFilterCargoType(data: OptionsClass) {
    const findCargoTypeIndex = this.cargoTypes.findIndex(
      (value) => value === data.value
    );

    if (findCargoTypeIndex !== -1) {
      this.cargoTypes.splice(findCargoTypeIndex, 1);
    } else {
      this.cargoTypes.push(data.value);
    }

    CargoController.setCargoTypes(this.cargoTypes);
    this.$emit("onChangeFilter");
  }

  filterCounter(value: string) {
    switch (value) {
      case "plane":
        return `(${this.cargoSuccessSummaryData.plane || 0})`;
      case "truck":
        return `(${this.cargoSuccessSummaryData.truck || 0})`;
      case "ship":
        return `(${this.cargoSuccessSummaryData.ship || 0})`;
    }
  }

  onChangeAdvancedFilter = debounce((event) => {
    CargoController.setAdvanceFilterData(event);
    CargoController.setFirstPage();
    this.$emit("onChangeFilter");
  }, 500);

  onResetAdvancedFilter = debounce((isFetch: boolean) => {
    CargoController.setAdvanceFilterData({
      type: { name: "", value: "" },
      cityOriginCode: "",
      cityDestinationCode: "",
      airport: ""
    });
    CargoController.setHubIdsSelected([]);
    CargoController.setFirstPage();
    if (isFetch) {
      this.$emit("onChangeFilter");
    }
  }, 500);

  // filter Date Range
  get periodeStart() {
    return CargoController.periodeStart;
  }

  get periodeEnd() {
    return CargoController.periodeEnd;
  }

  setPeriode(value: Array<any>) {
    CargoController.setPeriodeStart(value[0]);
    CargoController.setPeriodeEnd(value[1]);
    if (value[0] && value[1]) {
      CargoController.setFirstPage();
      this.$emit("onChangeFilter");
    }
  }

  // Multiple Select Filter
  hubIdSelected: any = [];
  hubIdOptions: any = [];

  async getHubList() {
    await HubController.getHubList(
      new RequestHubList({
        city:
          AccountController.accountData.account_type_detail?.partnerLocation
            ?.city_code,
        status: "active",
        partnerId: AccountController.accountData.account_type_detail?.id
      })
    ).then((res: any) => {
      this.hubIdOptions = res.data.map((item: any) => ({
        label: item.hubName,
        value: item.hubId
      }));
    });
  }

  // handle function download
  onChangeHubIdSelected = debounce(() => {
    const ids = this.hubIdSelected.length
      ? this.hubIdSelected.map((item: any) => Number(item.value))
      : [];
    CargoController.setHubIdsSelected(ids);
    CargoController.setFirstPage();
    this.$emit("onChangeFilter");
  }, 250);

  mapCargoType(type: string): string {
    const map: Record<string, string> = {
      plane: "pesawat",
      truck: "truk",
      ship: "kapal",
      train: "kereta"
    };

    return map[type];
  }

  get cargoListSuccessMapping() {
    const res: any = [];
    CargoController.cargoData.data.forEach((item: CargoData) => {
      const departure =
        item.cargoType === "plane" ? item.departureTime : item.departureDate;
      const arrival =
        item.cargoType === "plane" ? item.arrivalTime : item.arrivalDate;
      res.push({
        "No Kargo": item.cargoNumber,
        "Tipe Kargo":
          this.typeList.find((type) => type.value === item.cargoType)?.name ||
          "",
        "Kota Asal": `${item.originCityCode} - ${item.originCityName}`,
        "Kota Tujuan": `${item.destinationCityCode} - ${item.destinationCityName}`,
        "Kode Bandara Asal":
          item.cargoType === "plane"
            ? `${item.airportOriginCityCode} - ${item.airportOriginCityName}`
            : "-",
        "Kode Bandara Tujuan":
          item.cargoType === "plane"
            ? `${item.airportDestinationCityCode} - ${item.airportDestinationCityName}`
            : "-",
        "No seri kendaraan/No penerbangan": item.vehiclePoliceNumber,
        "Tanggal keberangkatan": this.$moment(item.departureDate).format(
          "DD MMMM YYYY"
        ),
        "Jam keberangkatan": departure
          ? this.$moment(departure)
              .utcOffset(departure)
              .format("HH:mm")
          : "-",
        "Tanggal kedatangan": this.$moment(item.arrivalDate).format(
          "DD MMMM YYYY"
        ),
        "Jam kedatangan": arrival
          ? this.$moment(arrival)
              .utcOffset(arrival)
              .format("HH:mm")
          : "-",
        "Total STT": item.totalStt,
        "Total Koli Kargo": item.totalPieces,
        "Chargeable weight kargo": item.cargoTotalCustomChargeableWeight,
        "Berat kotor kargo": item.cargoTotalCustomGrossWeight,
        "Berat volume kargo": item.cargoTotalCustomVolumeWeight,
        "Chargeable weight (Timbang Ulang)":
          item.cargoTotalEstimateChargeableWeight,
        "Total Berat kotor (Timbang Ulang)": item.cargoTotalEstimateGrossWeight,
        "Total Berat Dimensi Aktual": item.totalActualCargoVolumeWeight,
        "Total Berat volume (Timbang Ulang)":
          item.cargoTotalEstimateVolumeWeight,
        "Total Chargeable weight STT": item.cargoTotalChargeableWeight,
        "Total Berat kotor STT": item.cargoTotalGrossWeight,
        "Total Berat volume STT": item.cargoTotalVolumeWeight,
        "Tanggal dibuat": this.$moment(item.createdAt).format(
          "DD/MM/YYYY HH:mm"
        ),
        "Dibuat oleh": item.createdBy,
        "Tanggal diubah": item.updatedAt
          ? this.$moment(item.updatedAt).format("DD/MM/YYYY HH:mm")
          : "-",
        "Diubah oleh": item.updatedBy
      });
    });
    return res;
  }

  get cargoListFailedMapping() {
    const res: any = [];
    CargoController.retryCargo.data.forEach((item: any, index: number) => {
      res.push({
        "No Booking": item.bookingRequestId
          ? item.bookingRequestId
          : item.cargoNumber,
        "Tipe Kargo": this.mapCargoType(item.cargoType),
        "Status Kargo": item.cargoStatus === "failed" ? "Gagal" : "Dibatalkan",
        "Alasan Gagal": item.reasonFailed,
        "Kota Asal": item.originCityCode,
        "Kota Tujuan": item.destinationCityCode,
        "Kode Bandara Asal":
          item.cargoType === "plane"
            ? `${item.airportOriginCityCode} - ${item.airportOriginCityName}`
            : "",
        "Kode Bandara Tujuan":
          item.cargoType === "plane"
            ? `${item.airportDestinationCityCode} - ${item.airportDestinationCityName}`
            : "",
        "Tanggal Dibuat": item.createdAt,
        "Dibuat oleh": item.createdBy,
        "Lokasi Hub": item.cargoHubName
      });
    });
    return res;
  }

  async onDownload(status: boolean) {
    MainAppController.showLoading();
    const currentDate = this.$moment().format("YYYYMMDD");
    const epochTime = String(Date.now()).slice(-4);

    const fileSuffix = `${currentDate}${epochTime}`;

    const XLSX = await import("xlsx");
    const data = XLSX.utils.json_to_sheet(
      status ? this.cargoListFailedMapping : this.cargoListSuccessMapping
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      data,
      status ? "cargo_list_failed" : "cargo_list_success"
    );
    XLSX.writeFile(
      wb,
      status
        ? `Download data kargo gagal-${fileSuffix}.xlsx`
        : "cargo-list-success.xlsx"
    );
    MainAppController.closeLoading();
  }

  // mounted
  refs: any = "";
  mounted() {
    // (this.$refs.dateRange as any)?.reset();

    CargoController.setFirstPage();
    CargoController.setPlaneBooking(false);
    this.refs = this.$refs;
    if (this.isSuccessBookingTab) {
      CargoController.getCargoSuccessSummary();
    }
  }

  unmounted() {
    CargoController.setFirstPage();
    CargoController.onReset();
  }

  openDownloadListCargo = false;
}
